<template>
    <div class="dashboard-metric-graph">
        <!-- Compact period control, aligned with chart view toggles -->
        <div
            v-if="showDateFilter && graphData?.type === 'metric'"
            class="metric-period-toolbar flex justify-content-end align-items-center mb-2"
        >
            <Dropdown
                id="metric-date-range"
                v-model="selectedMonths"
                :options="dateRangeOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Period"
                class="metric-period-dropdown"
                v-tooltip.top="'Assessment date range for this chart'"
                @change="reloadMetricChart"
            />
        </div>

        <div v-if="loading" class="flex flex-column justify-content-center align-items-center">
            <h4 class="text-center">Loading...</h4>
            <ScaleLoader class="m-auto" />
        </div>
        <div v-else>
            <MetricGraph
                v-if="!loading"
                :type="graphType"
                :datasets="graphDatasets"
                :table-data="tableData"
                :graph-title="graphTitle"
                :users="users"
                :metricData="metricSeriesGraphData"
            />
        </div>
    </div>
</template>

<script setup>

import { computed, defineProps, onMounted, ref, watch } from 'vue';
import MetricGraph from '@/components/metrics/components/MetricGraph.vue';
import GlobalRequest from '@/api/global-request';
import store from '@/store';
import { DateTime } from 'luxon';

const props = defineProps({
    graphData: {
        type: Object,
        required: true,
    },
    // Initial date window for metrics. 0 = all time (no filter).
    defaultMonths: {
        type: Number,
        default: 3,
    },
    // Hide when parent (e.g. Add modal) already shows the date range control.
    showDateFilter: {
        type: Boolean,
        default: true,
    },
})

const dateRangeOptions = [
    { label: 'Last 3 months', value: 3 },
    { label: 'Last 6 months', value: 6 },
    { label: 'Last 12 months', value: 12 },
    { label: 'All time', value: 0 },
]

const selectedMonths = ref(props.defaultMonths ?? 3)

watch(() => props.defaultMonths, (value) => {
    selectedMonths.value = value ?? 3
})

const tableData = ref({});
const graphDatasets = ref({})
const graphType = ref('')
const graphTitle = ref('')

const userGroupUserList = ref([])
const loading = ref(false)
const users = ref([])
const metricSeriesGraphData = ref({})

const  metric = ref({
    id: null,
    company_id: null,
    name: null,
    code: null,
    description: null,
    type: null,
    sharable: 0,
    series: [],
    groups: [],
})

onMounted(() => {
    config(props.graphData)
})

watch(() => props.graphData, (newValue) => {
    config(newValue)
})

function reloadMetricChart() {
    if (props.graphData?.type === 'metric') {
        config(props.graphData)
    }
}

const company_id = computed(() => {
    return store.getters['auth/userCompanyId']?.id;
});

const sortedGroups = computed(() => {

    metric.value.groups.forEach(item => {

        //console.log('item.first', item.first);

        if (item.first!== null && item.procedure === 'values' && item.first.length > 0) {
            //console.log('TODO values first', item.first)
        } else if (item.first!== null && item.procedure === 'users' && item.first.length > 0) {
            userGroupUserList.value = [];

            const users = JSON.parse(item.first)

            if (typeof users !== 'undefined') {

                for (const user of users) {

                    GlobalRequest.getRequest('v2/admin/users/' + user, {
                        params: {
                            company_id: company_id.value,
                        },
                    }).then((response) => {
                        if (response.statusCode === 200) {
                            userGroupUserList.value.push(response.data.name + ' ' + response.data.surname)

                            //item.first = userList.join(' / ');

                        }
                    });
                }
            }
        }
    })

    let sorted = metric.value.groups;

    if (sorted !== null) {
        return sorted.sort((a, b) => {
            return a.order - b.order;
        })
    } else {
        return null;
    }
});



function config(data) {

    if (data.type === 'metric') {
        loading.value = true

        const params = {
            company_id: company_id.value,
        }
        // 0 / null = all time (no months query param)
        if (selectedMonths.value != null && selectedMonths.value > 0) {
            params.months = selectedMonths.value
        }

        GlobalRequest.getRequest('v2/datasource/metric/'+data.id, {params}).then((response) => {
            if (response.statusCode === 200) {
                graphType.value = response.data.metric.type;
                metricSeriesGraphData.value = response.data

                parsingMetricResponse(response)
            }
        }).catch(() => {

        }).finally(() => {
            loading.value = false
        })
    } else if (data.type === 'userGroup') {
        loading.value = true
        ///v2/datasource/users/{user_group_id}

        GlobalRequest.getRequest('v2/datasource/users/'+data.id, {params: {
                company_id: company_id.value,
            }}).then((response) => {
            if (response.statusCode === 200) {
                users.value = response.data.users
                graphTitle.value = response.data.userGroup.name

                graphType.value = 'counter'
            }
        }).catch(() => {

        }).finally(() => {
            loading.value = false
        })

    } else if (data.type === 'specialUserGroup') {
        loading.value = true

        GlobalRequest.getRequest(`/v2/datasource/special/user/group/${data.id}`, {params: {
                company_id: company_id.value,
            }}).then((response) => {

            if (response.statusCode === 200) {

                users.value = response.data.users
                graphTitle.value = response.data.data.name

                graphType.value = 'counter'
            }
        }).catch(() => {

        }).finally(() => {
            loading.value = false
        })
    }
}


function parsingMetricResponse(response) {

    metric.value.id = response.data.metric.id;
    metric.value.name = response.data.metric.name;
    metric.value.code = response.data.metric.code;
    metric.value.company_id = response.data.metric.company_id;
    metric.value.description = response.data.metric.description;
    metric.value.sharable = response.data.metric.sharable;
    metric.value.type = response.data.metric.type;
    metric.value.groups = response.data.metric.groups;
    metric.value.series = response.data.metric.series;

    graphTitle.value = response.data.metric.name

    graphDatasets.value = {
        labels: [],
        dataset: [],
        seriesLabel: '',
    };

    tableData.value = {
        headers: [],
        rows: [],
    };

    graphDatasets.value.dataset = new Map();

    metric.value.series.forEach((series) => {
        //const seriesId = series.id;
        const seriesLabel = series.label;

        if (Object.hasOwn(response.data.series.data, seriesLabel)) {
            series.result = response.data.series.data[seriesLabel].details.result;
            series.output = response.data.series.data[seriesLabel].details.output;

            series.tableData = buildTableData(response.data.series.data[seriesLabel].details.result, response.data.series.labels, seriesLabel)

            series.labels = response.data.series.labels;

            const dataset = buildDatasets(response.data.series.data[seriesLabel].details.result, sortedGroups.value, response.data.series.labels, series.label);

            graphDatasets.value.labels = buildLabels(response.data.series.data[seriesLabel].details.output, sortedGroups.value, response.data.series.labels, series.label)

            graphDatasets.value.seriesLabel = dataset.labels

            if (metric.value.series.length === 1) {
                // Only one series: maybe there are multiple groups. Display it as single series.
                graphDatasets.value.dataset = dataset.dataset
            } else {
                dataset.dataset.forEach((item) => {
                    graphDatasets.value.dataset.set(seriesLabel, item)
                })
            }

            // Each series should be grouped with the same logic so the columns should match.

            series.tableData.headers.forEach(item => {

                // deep copy to make them independent
                let data = JSON.parse(JSON.stringify(item))

                if (data.field.substring(0, 6) === 'answer') {
                    data.field = 'answer_'+series.label.toLowerCase().replaceAll(/ /g, '_')
                }

                let found = false

                tableData.value.headers.forEach(tableHeader => {
                    if (tableHeader.field === data.field) {
                        found = true;
                    }
                })

                if (!found) {
                    tableData.value.headers.push(data)
                }

            })

            series.tableData.rows.forEach((item, index) => {
                // deep copy to make them independent
                let data = JSON.parse(JSON.stringify(item))

                for (const prop in data) {
                    let field = prop;

                    if (field === 'answer') {
                        field = 'answer_'+series.label.toLowerCase().replaceAll(/ /g, '_')
                        data[field] = data.answer
                        delete data.answer
                    }

                    if (typeof tableData.value.rows[index] === 'undefined') {
                        tableData.value.rows[index] = {}
                    }

                    tableData.value.rows[index][field] = data[field]
                }
            })


        }
    })

    tableData.value.headers.sort((a, b) => a.field.localeCompare(b.field));
}


function buildTableData(resultData, labels, seriesLabel) {
    let headers = []

    if (resultData.length > 0) {
        const firstTableRow = resultData[0]

        for (const prop in firstTableRow) {

            let found = false;

            if (labels.length > 0) {
                labels.forEach(header => {
                    /*
                    If there are multiple series, all of them will share all the keys based on groups.
                    Removing duplicates.
                     */

                    if (prop === header.field) {
                        found = true;
                    }
                })
            }

            if (!found) {
                if (prop === 'groupBySex') {
                    headers.push({ field: prop, header: 'Sex' });
                } else if (prop === 'groupByPassFail') {
                    headers.push({ field: prop, header: 'Pass / fail' });
                } else if (prop === 'age_today_range') {
                    headers.push({ field: prop, header: 'Today age range' });
                } else if (prop === 'age_assessment_range') {
                    headers.push({ field: prop, header: 'Assessment date age range' });
                } else if (prop === 'groupByUsers') {
                    headers.push({ field: prop, header: 'Users' });
                } else if (prop.substring(0, 8) === 'calendar') {
                    headers.push({ field: prop, header: 'Calendar' });
                } else if (prop === 'groupByValue') {
                    headers.push({ field: prop, header: 'Value' });
                } else if (prop === 'groupByCompanyPool') {
                    headers.push({ field: prop, header: 'Companies' });
                } else if (prop.substring(0, 6) === 'answer') {
                    headers.push({ field: prop, header: seriesLabel });
                } else if (prop === 'values') {
                    headers.push({ field: prop, header: 'Value' });
                }
            }
        }
    }

    // deep copy to make them independent
    let rows = JSON.parse(JSON.stringify(parseGroupTokens(resultData, labels)))

    return {
        rows: rows,
        headers: headers
    }
}

function parseGroupTokens(data, labels) {

    data.forEach((item) => {
        for (const prop in item) {
            if (prop === 'groupBySex' ) {
                if (Object.hasOwn(labels, 'groupBySex')) {
                    const value = item['groupBySex'];
                    if (value === null) {
                        item['groupBySex'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupBySex'], value)) {
                        item['groupBySex'] = labels['groupBySex'][value];
                    }
                }
            } else if (prop === 'groupByValue' ) {
                if (Object.hasOwn(labels, 'groupByValue')) {
                    const value = item['groupByValue'];
                    if (value === null) {
                        item['groupByValue'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByValue'], value)) {
                        item['groupByValue'] = labels['groupByValue'][value];
                    }
                }
            } else if (prop === 'groupByCompanyPool' ) {
                if (Object.hasOwn(labels, 'groupByCompanyPool')) {
                    const value = item['groupByCompanyPool'];
                    if (value === null) {
                        item['groupByCompanyPool'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByCompanyPool'], value)) {
                        item['groupByCompanyPool'] = labels['groupByCompanyPool'][value];
                    }
                }
            } else if (prop === 'age_today_range') {
                if (Object.hasOwn(labels, 'age_today_range')) {
                    const value = item['age_today_range'];
                    if (value === null) {
                        item['age_today_range'] = 'N.A.';
                    } else if (Object.hasOwn(labels['age_today_range'], value)) {
                        item['age_today_range'] = labels['age_today_range'][value];
                    }
                }
            } else if (prop === 'age_assessment_range') {
                if (Object.hasOwn(labels, 'age_assessment_range')) {
                    const value = item['age_assessment_range'];
                    if (value === null) {
                        item['age_assessment_range'] = 'N.A.';
                    } else if (Object.hasOwn(labels['age_assessment_range'], value)) {
                        item['age_assessment_range'] = labels['age_assessment_range'][value];
                    }
                }
            } else if (prop === 'groupByPassFail') {
                if (Object.hasOwn(labels, 'groupByPassFail')) {
                    const value = item['groupByPassFail'];
                    if (value === null) {
                        item['groupByPassFail'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByPassFail'], value)) {
                        item['groupByPassFail'] = labels['groupByPassFail'][value];
                    }
                }
            } else if (prop === 'groupByUsers') {
                if (Object.hasOwn(labels, 'groupByUsers')) {
                    const value = item['groupByUsers'];
                    if (value === null) {
                        item['groupByUsers'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByUsers'], value)) {
                        item['groupByUsers'] = labels['groupByUsers'][value];
                    }
                }
            } else if (prop.substring(0, 10) === 'calendar__' ) {
                //Original string: calendar___238
                //the first part is constant, the second will be the group id

                const separator = '___';
                const procedureFragments = prop.split(separator);
                const value = item[prop];

                sortedGroups.value.forEach((group) => {
                    if (group.id!==null  && group.id.toString() === procedureFragments[1].toString()) {

                        if (typeof value === 'number' && group.first === 'days') {

                            // The integer part of the integer division will return the year,
                            // the remainder the day's number (1 to 366). 0 mean the last day of a leap year.
                            //
                            // E.g. 740886
                            // Days: 2024-04-11 , 2024-04-10 , 2024-04-09
                            //
                            // Day's number is the last day of the interval.

                            const year = Math.trunc(value / 366);
                            const day = value % 366;

                            item[prop] = value;

                            if (value > 0 && year > 0) {
                                item[prop] = DateTime.fromObject({
                                        year: year,
                                        ordinal: day
                                    }).toFormat('dd-MM-yyyy') + ' to '
                                    + DateTime.fromObject({
                                        year: year,
                                        ordinal: day - (group.second - 1)
                                    }).toFormat('dd-MM-yyyy');
                            }

                        } if (typeof value === 'number' && group.first === 'years') {
                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = value - (group.second - 1);

                                item[prop] = start + ' to ' + value;
                            } else {
                                item[prop] = value
                            }
                        } else if (typeof value === 'number' && group.first === 'months') {
                            const year = Math.trunc(value / 12);
                            const month = value % 12;

                            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul","Aug", "Sep", "Oct", "Nov", "Dec"];

                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = month - (group.second - 1);

                                item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                            } else {
                                item[prop] = monthNames[month-1] + ' ('+year+')'
                            }
                        } else if (typeof value === 'number' && group.first === 'weeks') {
                            //const year = Math.trunc(value / 12);
                            //const month = value % 12;


                            const year = value.toString().substring(0, 4)
                            const week = parseInt(value.toString().substring(4, 6) ); // *1 to convert it into a number

                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = week - (group.second - 1);

                                item[prop] = 'From week ' + start + ' to ' + week + ' ('+year+')'

                                //item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                            } else {
                                item[prop] = 'Week ' + week + ' ('+year+')'
                            }


                        } else {
                            //console.log('unrecognised group', group.first);
                        }
                    }
                })

            } else if (prop !== 'answer') {
                //console.log('unhandled prop', prop)
            }
        }
    })

    return data
}

function buildDatasets(seriesOutput, groupDefinition, seriesLabels, metricName) {
    let dataset = new Map()
    let labels = new Map()

    // Looking for the inner group. If it doesn't exist, use the only one that exists.
    const groupIndex = groupDefinition.length - 1;

    //console.log('buildDatasets / seriesOutput', seriesOutput )

    if (typeof groupDefinition[groupIndex] != 'undefined') {
        // At least a group is defined

        const groupProcedure = groupDefinition[groupIndex].procedure;

        seriesOutput.forEach((item) => {
            let key = '';

            if (groupProcedure === 'sex') {
                key = 'groupBySex';
            } else if (groupProcedure === 'age_today_range') {
                key = 'age_today_range';
            } else if (groupProcedure === 'age_assessment_range') {
                key = 'age_assessment_range';
            } else if (groupProcedure === 'pass_fail') {
                key = 'groupByPassFail';
            } else if (groupProcedure === 'users') {
                key = 'groupByUsers';
            } else if (groupProcedure === 'companies') {
                key = 'groupByCompanyPool';
            } else if (groupProcedure === 'values') {
                key = 'groupByValue';
            } else if (groupProcedure === 'calendar') {
                /*
                Output: calendar___300 to match a record like answer:
                "77.875000", groupBySex: "Female", calendar___300: "Aug (2024)"
                 */
                key = groupProcedure + '___' + groupDefinition[groupIndex].id //id
            }

            let groupKey = null

            if (groupIndex === 0) {
                // only one group level
                groupKey = 0
                labels.set(groupKey, metricName)
            } else {

                groupKey = item[key]

                if (Object.hasOwn(seriesLabels, key)) {
                    if (typeof seriesLabels[key][groupKey] === 'undefined') {
                        labels.set(groupKey, item[key]);
                    } else {
                        labels.set(groupKey, seriesLabels[key][groupKey]);
                    }
                }
            }

            let seriesData = dataset.get(groupKey)
            if (typeof seriesData === 'undefined') {
                seriesData = [];
            }
            seriesData.push(item.answer)
            dataset.set(groupKey, seriesData);

        })

        return {
            dataset: dataset, labels: labels
        };
    } else {
        //console.log('seriesOutput', seriesOutput);

        let groupKey = 0
        labels.set(groupKey, metricName)

        let seriesData = [];

        seriesOutput.forEach((item) => {
            seriesData.push(item.answer)
            dataset.set(groupKey, seriesData);
        })

        return {
            dataset: dataset, labels: labels
        }
    }
}

function buildLabels(seriesOutput, groupDefinition, staticLabels, metricName) {
    let labels = [];

    if (groupDefinition.length > 0) {
        const firstGroup = groupDefinition[0]

        if (firstGroup.procedure === 'sex') {
            if (Object.hasOwn(staticLabels, 'groupBySex')) {
                // Adding N.A.
                labels[0] = 'N.A.'

                for (const key in staticLabels.groupBySex) {
                    const label = staticLabels.groupBySex[key]
                    labels.push(label)
                }
            }

        } else if (firstGroup.procedure === 'age_today_range') {
            if (Object.hasOwn(staticLabels, 'age_today_range')) {
                if (Object.hasOwn(seriesOutput, 'age_today_range')) {
                    for (const key in seriesOutput.age_today_range) {
                        const label = staticLabels.age_today_range[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'values') {

            if (Object.hasOwn(staticLabels, 'groupByValue')) {
                if (Object.hasOwn(seriesOutput, 'groupByValue')) {
                    for (const key in seriesOutput.groupByValue) {
                        let label = staticLabels.groupByValue[key]

                        if (typeof label === 'undefined') {
                            label = 'N.A.'
                        }

                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'pass_fail') {
            if (Object.hasOwn(staticLabels, 'groupByPassFail')) {
                if (Object.hasOwn(seriesOutput, 'groupByPassFail')) {
                    for (const key in seriesOutput.groupByPassFail) {
                        const label = staticLabels.groupByPassFail[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'users') {
            if (Object.hasOwn(staticLabels, 'groupByUsers')) {
                if (Object.hasOwn(seriesOutput, 'groupByUsers')) {
                    for (const key in seriesOutput.groupByUsers) {
                        const label = staticLabels.groupByUsers[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'companies') {
            if (Object.hasOwn(staticLabels, 'groupByCompanyPool')) {
                // console.log('seriesOutput', seriesOutput);
                if (Object.hasOwn(seriesOutput, 'groupByCompanyPool')) {
                    for (const key in seriesOutput.groupByCompanyPool) {
                        const label = staticLabels.groupByCompanyPool[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'age_assessment_range') {
            if (Object.hasOwn(staticLabels, 'age_assessment_range')) {
                if (Object.hasOwn(seriesOutput, 'age_assessment_range')) {
                    for (const key in seriesOutput.age_assessment_range) {
                        const label = staticLabels.age_assessment_range[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'calendar') {
            /*
            The calendar group will have the following signature:
            calendar___<id>
            E.g., calendar___46
             */

            const groupKey = 'calendar___'+firstGroup.id
            if (Object.hasOwn(seriesOutput, groupKey)) {

                const calendarGroup = firstGroup.first
                const calendarQty = firstGroup.second

                for (const value in seriesOutput[groupKey]) {

                    if (calendarGroup === 'weeks') {
                        const year = value.toString().substring(0, 4)
                        const week = parseInt(value.toString().substring(4, 6) ); // *1 to convert it into a number

                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = week - (firstGroup.second - 1);

                            labels.push('From week ' + start + ' to ' + week + ' ('+year+')')

                            //item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                        } else {
                            labels.push('Week ' + week + ' ('+year+')')
                        }
                    } else if (calendarGroup === 'months') {
                        const year = Math.floor((value - 1) / 12);
                        const month = ((value - 1) % 12) + 1;

                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul","Aug", "Sep", "Oct", "Nov", "Dec"];

                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = month - (firstGroup.second - 1);

                            labels.push(monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')');
                        } else {
                            labels.push(monthNames[month-1] + ' ('+year+')')
                        }
                    } else if (calendarGroup === 'years') {
                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = value - (firstGroup.second - 1);

                            labels.push(start + ' to ' + value);
                        } else {
                            labels.push(value)
                        }
                    } else if (calendarGroup === 'days') {
                        const qty = parseInt(calendarQty)

                        if (!isNaN(qty) && qty > 0) {
                            const year = Math.trunc(value / 366);
                            const day = value % 366;

                            if (value > 0 && year > 0) {
                                labels.push(
                                    DateTime.fromObject({
                                        year: year,
                                        ordinal: day
                                    }).toFormat('dd-MM-yyyy') + ' to '
                                    + DateTime.fromObject({
                                        year: year,
                                        ordinal: day - (calendarQty - 1)
                                    }).toFormat('dd-MM-yyyy')
                                );
                            }
                        }
                    }
                }
            }
        } else {
            //console.log('firstGroup.procedure', firstGroup.procedure)
        }
    } else {
        labels.push(metricName)
    }


    return labels;
}
</script>

<style scoped lang="scss">
.metric-period-toolbar {
    min-height: 2.5rem;
}

.metric-period-dropdown {
    min-width: 10.5rem;
    width: 10.5rem;
}
</style>